import { z } from 'zod';
/**
 * Environment-variable schema for every process in the register
 * (api, worker, migration runner, scripts).
 *
 * Anything that reads `process.env` must go through `loadEnv` so that
 * misconfiguration fails fast at boot — never at the first request.
 *
 * Values are loaded from `.env` in local dev. (Production deployment is
 * out of scope for v1 — the focus is local-dev ergonomics.)
 */
const url = z.string().url();
const port = z.coerce.number().int().min(1).max(65535);
const nonEmpty = z.string().min(1);
const secret = z.string().min(32, 'must be at least 32 characters (use `openssl rand -hex 32`)');
const bool = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1');
const optionalString = z
    .string()
    .optional()
    .transform((v) => (v === '' ? undefined : v));
export const envSchema = z
    .object({
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
    LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),
    API_PORT: port.default(3001),
    API_PUBLIC_URL: url,
    WEB_PUBLIC_URL: url,
    DATABASE_URL: nonEmpty,
    DATABASE_MIGRATOR_URL: nonEmpty,
    S3_ENDPOINT: url,
    /**
     * Browser-facing host for presigned URLs. In production this is unset and
     * the SDK signs against `S3_ENDPOINT` (which is the public S3 URL anyway).
     * In local dev the API talks to MinIO over the docker network at
     * `http://devenv-minio-1:9000`, but the browser running on the host can't
     * resolve that name — set this to the host-reachable URL (e.g.
     * `http://localhost:9000`) so the signed URL the browser PUTs to is
     * actually reachable. Signature is bound to whichever host is in the URL,
     * so dev MinIO must be reachable at this exact host:port.
     */
    S3_PUBLIC_ENDPOINT: url.optional(),
    S3_REGION: nonEmpty,
    // Both optional: when present (local dev against MinIO) the SDK is given
    // explicit static creds; when absent (App Runner / ECS) the SDK's default
    // credential provider chain resolves the task-role's short-lived creds.
    S3_ACCESS_KEY_ID: optionalString,
    S3_SECRET_ACCESS_KEY: optionalString,
    S3_BUCKET_EVIDENCE: nonEmpty,
    S3_BUCKET_EXPORTS: nonEmpty,
    S3_FORCE_PATH_STYLE: bool.default(false),
    SMTP_HOST: nonEmpty,
    SMTP_PORT: port.default(587),
    SMTP_USER: optionalString,
    SMTP_PASSWORD: optionalString,
    SMTP_FROM: nonEmpty,
    JWT_SECRET: secret,
    /**
     * Shared secret EventBridge Scheduler sends in the `X-Cron-Secret`
     * header on every internal-cron POST. The same value is provisioned
     * on both sides by the `eventbridge-cron` Terraform module (issued
     * by `random_password`, stored once in SSM, embedded in the
     * EventBridge Connection auth, injected into App Runner via
     * `runtime_environment_secrets`). Validated with `crypto.timingSafeEqual`
     * by the route middleware. See `issues/eventbridge-cron-trigger.md`.
     */
    CRON_SHARED_SECRET: secret,
    BOOTSTRAP_ADMIN_EMAIL: optionalString,
    BOOTSTRAP_ADMIN_FULL_NAME: optionalString,
})
    .superRefine((env, ctx) => {
    // Both-or-neither for the S3 static-cred pair. Setting only one is almost
    // always a misconfiguration — fail at boot rather than at the first S3 call.
    const hasKey = env.S3_ACCESS_KEY_ID !== undefined;
    const hasSecret = env.S3_SECRET_ACCESS_KEY !== undefined;
    if (hasKey !== hasSecret) {
        const message = 'S3_ACCESS_KEY_ID and S3_SECRET_ACCESS_KEY must be set together (both for static creds against MinIO/IAM-user; both omitted to use the AWS default credential chain, e.g. App Runner task role)';
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: [hasKey ? 'S3_SECRET_ACCESS_KEY' : 'S3_ACCESS_KEY_ID'],
        });
    }
});
/**
 * Validates `source` against `envSchema` and returns the parsed env.
 * Defaults to `process.env` so callers can write `loadEnv()` at boot.
 *
 * Throws `EnvValidationError` (a subclass of `Error`) with a
 * human-readable list of failed keys. Catch and exit non-zero at the
 * top level — never swallow.
 */
export function loadEnv(source = process.env) {
    const result = envSchema.safeParse(source);
    if (!result.success) {
        throw new EnvValidationError(result.error);
    }
    return result.data;
}
export class EnvValidationError extends Error {
    issues;
    constructor(zodError) {
        const issues = zodError.issues.map((i) => ({
            path: i.path.join('.'),
            message: i.message,
        }));
        const summary = issues.map((i) => `  - ${i.path || '<root>'}: ${i.message}`).join('\n');
        super(`Invalid environment configuration:\n${summary}`);
        this.name = 'EnvValidationError';
        this.issues = issues;
    }
}
//# sourceMappingURL=env.js.map